/**
 * Utilities for detecting auth and coin errors from server action results,
 * and broadcasting them as custom DOM events so global watchers can react
 * (e.g., opening the auth modal or buy-coins modal).
 */

const AUTH_ERROR_MESSAGE = 'Not authenticated';
const AUTH_EXPIRED_EVENT = 'auth:session-expired';
const INSUFFICIENT_COINS_EVENT = 'wallet:insufficient-coins';

export function isAuthError(error?: string | null): boolean {
  return error === AUTH_ERROR_MESSAGE;
}

export function isInsufficientCoinsError(error?: string | null): boolean {
  if (typeof error !== 'string') return false;
  return (
    error.includes('Insufficient') ||
    (error.includes('Need') && /\b(roses|coins)\b/.test(error))
  );
}

export interface InsufficientCoinsDetail {
  /** Coins the attempted action costs (parsed from the error message). */
  neededCoins?: number;
  /** User's coin balance at the time of the error. */
  currentBalance?: number;
}

/**
 * Pull the "Need X, have Y" amounts out of an insufficient-coins error so the
 * out-of-coins popup can tell the user exactly how short they are. Returns an
 * empty object when the message doesn't carry the numbers.
 */
function parseInsufficientCoinsDetail(
  error?: string | null,
): InsufficientCoinsDetail {
  if (typeof error !== 'string') return {};
  const match = error.match(/Need\s+(\d+),\s*have\s+(\d+)/i);
  if (!match) return {};
  return { neededCoins: Number(match[1]), currentBalance: Number(match[2]) };
}

export function fireAuthExpiredEvent(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
  }
}

export function fireInsufficientCoinsEvent(
  detail?: InsufficientCoinsDetail,
): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(INSUFFICIENT_COINS_EVENT, { detail: detail ?? {} }),
    );
  }
}

/**
 * Check if a server action result is an auth error.
 * If so, fire the session-expired event and return true.
 * Use this in error handling to skip showing a generic error toast.
 */
export function checkAuthError(result: { error?: string | null }): boolean {
  if (isAuthError(result.error)) {
    fireAuthExpiredEvent();
    return true;
  }
  return false;
}

/**
 * Check if a server action result is an insufficient coins error.
 * If so, fire the insufficient-coins event and return true.
 */
export function checkCoinsError(result: { error?: string | null }): boolean {
  if (isInsufficientCoinsError(result.error)) {
    fireInsufficientCoinsEvent(parseInsufficientCoinsDetail(result.error));
    return true;
  }
  return false;
}

/**
 * Daily free-message cap (admin-tunable, subscribers exempt). The server
 * returns this exact error string; the watcher opens the subscription upsell.
 */
export const FREE_LIMIT_ERROR = 'FREE_LIMIT_REACHED';
const FREE_LIMIT_EVENT = 'chat:free-limit';

export function fireFreeLimitEvent(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FREE_LIMIT_EVENT));
  }
}

/**
 * Check if a server action result is the daily free-message limit.
 * If so, fire the free-limit event (opens the upgrade wall) and return true.
 */
export function checkFreeLimitError(result: {
  error?: string | null;
}): boolean {
  if (result.error === FREE_LIMIT_ERROR) {
    fireFreeLimitEvent();
    return true;
  }
  return false;
}

// Re-export event names for watchers
export const AUTH_EXPIRED_EVENT_NAME = AUTH_EXPIRED_EVENT;
export const INSUFFICIENT_COINS_EVENT_NAME = INSUFFICIENT_COINS_EVENT;
export const FREE_LIMIT_EVENT_NAME = FREE_LIMIT_EVENT;
